import { FieldShell } from '@today/ui-components'
import { Input, Textarea } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { ProductOption } from './product-types'

/**
 * Field helpers of the product organisms: the `Input`/`Textarea` primitives and a
 * native select inside `FieldShell` (label, error, a11y wiring). `SelectField`
 * is not reused because the product forms need a disabled (read-only) state
 * and a selectable "none"/"any" option, which it does not offer.
 */

const SELECT =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text disabled:cursor-not-allowed disabled:opacity-50'

/** A labelled text input with an optional error (shared by the product views). */
export function TextInput(props: {
	label: string
	value: string
	error?: string
	disabled?: boolean
	onChange: (value: string) => void
}): ReactElement {
	return (
		<FieldShell label={props.label} error={props.error}>
			<Input value={props.value} disabled={props.disabled} onChange={(e) => props.onChange(e.target.value)} />
		</FieldShell>
	)
}

/** A labelled multi-line input with an optional error and hint. */
export function TextArea(props: {
	label: string
	value: string
	error?: string
	description?: string
	disabled?: boolean
	onChange: (value: string) => void
}): ReactElement {
	return (
		<FieldShell label={props.label} error={props.error} description={props.description}>
			<Textarea value={props.value} disabled={props.disabled} onChange={(e) => props.onChange(e.target.value)} />
		</FieldShell>
	)
}

/** A labelled native select whose first option is an explicit "none"/"any" choice. */
export function SelectInput(props: {
	label: string
	value: string
	emptyLabel?: string
	options: ProductOption[]
	error?: string
	disabled?: boolean
	onChange: (value: string) => void
}): ReactElement {
	return (
		<FieldShell label={props.label} error={props.error}>
			<select
				className={SELECT}
				value={props.value}
				disabled={props.disabled}
				onChange={(e) => props.onChange(e.target.value)}
			>
				{props.emptyLabel !== undefined ? <option value="">{props.emptyLabel}</option> : null}
				{props.options.map((o) => (
					<option key={o.id} value={o.id}>
						{o.label}
					</option>
				))}
			</select>
		</FieldShell>
	)
}

/** An inline error banner (role=alert) or nothing. */
export function ErrorText({ message }: { message: string | null | undefined }): ReactElement | null {
	return message ? (
		<p role="alert" className="text-ds-sm text-ds-danger">
			{message}
		</p>
	) : null
}
